import { useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TopWorkflowsResponse } from '@/types/composer-dashboard';
import { AlertCircle, Workflow } from 'lucide-react';
import { SectionHeading } from './section-heading';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface TopWorkflowsSectionProps {
  data: TopWorkflowsResponse | undefined;
  isLoading?: boolean;
  error?: Error | null;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return `${Math.floor(diffDays / 30)} months ago`;
}

export function TopWorkflowsSection({
  data,
  isLoading = false,
  error,
}: TopWorkflowsSectionProps) {
  const { isDashboardTopWorkflowsCostEnabled } = useLaunchDarkly();
  const tableRef = useRef<HTMLDivElement>(null);

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <div className='rounded-md border'>
          <div className='space-y-4 p-6'>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className='h-10 w-full' />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='space-y-4'>
        <SectionHeading className='flex items-center gap-2'>
          <Workflow className='size-5' />
          Most Executed Workflows
        </SectionHeading>
        <Card className='border-destructive/50 bg-destructive/5'>
          <CardContent className='flex items-center gap-3 p-6'>
            <AlertCircle className='size-5 text-destructive' />
            <div>
              <p className='font-medium text-destructive'>
                Failed to load top workflows
              </p>
              <p className='text-sm text-muted-foreground'>
                {error.message ||
                  'An error occurred while fetching workflow data'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className='space-y-4'>
      <SectionHeading className='flex items-center gap-2'>
        <Workflow className='size-5' />
        Most Executed Workflows
      </SectionHeading>

      <div className='rounded-md border'>
        <Table tableRef={tableRef}>
          <TableHeader>
            <TableRow>
              <TableHead className='w-[50px]'>#</TableHead>
              <TableHead>Workflow</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead className='text-right'>Executions</TableHead>
              {isDashboardTopWorkflowsCostEnabled && (
                <TableHead className='text-right'>Total Cost</TableHead>
              )}
              <TableHead>Last Run</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.workflows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isDashboardTopWorkflowsCostEnabled ? 6 : 5}
                  className='text-center text-muted-foreground py-8'
                >
                  No workflows found
                </TableCell>
              </TableRow>
            ) : (
              data.workflows.map((workflow, index) => (
                <TableRow key={workflow.workflow_id}>
                  <TableCell className='font-medium text-muted-foreground'>
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className='font-medium'>
                        {workflow.workflow_name}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className='text-sm'>
                        {workflow.owner_name || 'Unknown'}
                      </div>
                      {workflow.owner_email && (
                        <div className='text-xs text-muted-foreground'>
                          {workflow.owner_email}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className='text-right'>
                    <span className='font-semibold'>
                      {workflow.execution_count.toLocaleString()}
                    </span>
                  </TableCell>
                  {isDashboardTopWorkflowsCostEnabled && (
                    <TableCell className='text-right'>
                      <span className='text-sm tabular-nums'>
                        {workflow.total_cost_usd != null
                          ? `$${workflow.total_cost_usd.toFixed(2)}`
                          : '—'}
                      </span>
                    </TableCell>
                  )}
                  <TableCell>
                    <div>
                      <div className='text-sm'>
                        {formatDate(workflow.last_run_at)}
                      </div>
                      {workflow.last_run_at && (
                        <div className='text-xs text-muted-foreground'>
                          {formatTimeAgo(workflow.last_run_at)}
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
